import { useEffect, useState, useCallback } from 'react';
import Flex from '@deprecated-ui/common/Flex';
import color from '@deprecated-ui/_style/color';
import shallow from 'zustand/shallow';
import handleErrorMessageAPI from 'global/AlertErrorMessage';
import ERROR_MESSAGE from 'constants/errorMessage';
import If from 'global/ErrorHandlers/If';
import useGetPortfolioData from 'hooks/useGetPortfolioData';
import {
  virtualTradingNavigate,
  VIRTUAL_TRADING_SOURCE,
  VIRTUAL_TRADING_TYPES,
} from 'lib/analytics/virtualTrading';
import { ORDER_TYPE } from 'constants/trading';
import tradingDataStore from 'lib/stores/tradingData';
import usePortfolioStore from 'global/VirtualTrading/stores/usePortfolioStore';
import {
  ModalFormWrapper,
  TransactionModalContainer,
  FormWrapper,
  FreezeLayer,
  ModalTab,
  ModalTabText,
} from '../styled';
import SellFormVirtual from './FormSell';
import BuyFormVirtual from './FormBuy';
import FormHeader from '../components/HeaderVirtual';
import TableOrderbook from '../TableOrderbook';
import tradingModalStore from '../store';
import virtualTradingStore from './store';

type BuySellModalProps = {
  closeModal: () => void;
  // eslint-disable-next-line react/require-default-props
  onSwitchTab?: (prop: string) => void;
};

function BuySellModal({ closeModal, onSwitchTab }: BuySellModalProps) {
  const {
    symbol,
    orderType,
    price: priceProps,
    showOrderbook,
    showTabs,
    orderedLot,
    orderid,
    orderedPrice,
    showModal: showTradingModal,
  } = tradingModalStore();

  const { resetOrderData, setPrice, setStockCode, setShares } =
    virtualTradingStore();
  const { loadPortfolioDetail } = usePortfolioStore();

  const [isSearchActive, setIsSearchActive] = useState<boolean>(false);

  const { company, getPortfolio, getCompany, resetCompany } = tradingDataStore(
    (s) => ({
      company: s.company,
      getPortfolio: s.getPortfolio,
      getCompany: s.getCompany,
      resetCompany: s.resetCompany,
    }),
    shallow,
  );

  const { isLoading, data } = company;

  const portfolioData = useGetPortfolioData(symbol);
  const available_lot = portfolioData?.available_lot || 0;

  const handleNotTradeable = () => {
    handleErrorMessageAPI(
      'The symbol is not tradeable',
      ERROR_MESSAGE.ALERT_RED,
    );
    closeModal();
  };

  // TODO: MOVE LOGIC INIT DATA TO BUYFORM AND SELLFORM
  const setupInitialData = useCallback(async () => {
    setIsSearchActive(false);
    resetOrderData();
    await getPortfolio(false);
    const getData = await getCompany(symbol);

    // @ts-ignore
    const { error, data: result } = getData;
    if (error) return;
    const { tradeable, price, symbol: symbolCompany } = result;

    const isTradeable = Number(tradeable) === 1;
    if (!isTradeable) {
      handleNotTradeable();
      return;
    }

    // Set new refference due to bugs
    // refferencing to toher zustand state symbol
    setStockCode(symbolCompany);
    if (orderType === ORDER_TYPE.VIRTUAL_AMEND_BUY) {
      setShares(orderedLot * 100);
      setPrice(orderedPrice);
    } else if (orderType === ORDER_TYPE.VIRTUAL_BUY) {
      setShares(0);
      setPrice(priceProps || price);
    }
  }, [orderType, symbol]);

  const onCloseModal = async () => {
    await resetOrderData();
    resetCompany();
    closeModal();
  };

  const isBuy =
    orderType === ORDER_TYPE.VIRTUAL_BUY ||
    orderType === ORDER_TYPE.VIRTUAL_AMEND_BUY;
  const isSell =
    orderType === ORDER_TYPE.VIRTUAL_SELL ||
    orderType === ORDER_TYPE.VIRTUAL_AMEND_SELL;

  useEffect(() => {
    if (symbol) {
      setupInitialData();
      loadPortfolioDetail(symbol);
    }
  }, [symbol]);

  useEffect(() => {
    if (isSell) {
      virtualTradingNavigate.onSellOrderViewed(
        symbol,
        VIRTUAL_TRADING_TYPES.virtualTrading,
      );
    }
  }, [isSell]);

  useEffect(() => {
    const currentUrl = window.location.pathname.toLowerCase();
    let source;
    if (currentUrl.includes('symbol')) {
      source = VIRTUAL_TRADING_SOURCE.companyPage;
    } else if (currentUrl.includes('stream') || currentUrl.includes('post')) {
      source = VIRTUAL_TRADING_SOURCE.predictionPost;
    } else if (currentUrl.includes('portfolio')) {
      source = VIRTUAL_TRADING_SOURCE.portfolio;
    } else if (currentUrl.includes('watchlist')) {
      source = VIRTUAL_TRADING_SOURCE.watchlist;
    }
    if (isBuy) {
      virtualTradingNavigate.onBuyOrderViewes(
        symbol,
        source,
        VIRTUAL_TRADING_TYPES.virtualTrading,
      );
    }
  }, [isBuy]);

  if (isLoading || !data?.price) return <div />;
  return (
    <TransactionModalContainer
      data-cy="transaction-modal-virtual"
      visible
      onCancel={onCloseModal}
    >
      <Flex>
        <If condition={showOrderbook}>
          <TableOrderbook />
        </If>
        <ModalFormWrapper>
          {isSearchActive && (
            <FreezeLayer onClick={() => setIsSearchActive(false)} />
          )}
          {isLoading && <FreezeLayer />}
          <FormWrapper style={showTabs ? { paddingTop: 0 } : {}}>
            <If condition={showTabs && onSwitchTab}>
              <Flex style={{ margin: '-15px -20px' }}>
                <ModalTab
                  isActive={isBuy}
                  onClick={() => onSwitchTab(ORDER_TYPE.VIRTUAL_BUY)}
                >
                  <ModalTabText color={color.primary}>Buy</ModalTabText>
                </ModalTab>
                <ModalTab
                  isActive={isSell}
                  onClick={() => {
                    if (available_lot > 0) onSwitchTab(ORDER_TYPE.VIRTUAL_SELL);
                  }}
                >
                  <ModalTabText color={color.sunsetOrange}>Sell</ModalTabText>
                </ModalTab>
              </Flex>
            </If>

            <FormHeader
              orderType={orderType}
              showTitle={!showTabs}
              onUpdateSymbol={(newSymbol) => {
                showTradingModal({
                  symbol: newSymbol,
                  // Chnaging symbol always open buy modal by default
                  orderType: ORDER_TYPE.VIRTUAL_BUY,
                });
              }}
              isSearchActive={isSearchActive}
              setIsSearchActive={() => setIsSearchActive(!isSearchActive)}
            />

            {isBuy && (
              <BuyFormVirtual
                orderType={orderType}
                orderid={orderid}
                closeModal={onCloseModal}
              />
            )}

            {isSell && (
              <SellFormVirtual
                orderType={orderType}
                orderid={orderid}
                orderedLot={orderedLot}
                orderedPrice={orderedPrice}
                closeModal={onCloseModal}
              />
            )}
          </FormWrapper>
        </ModalFormWrapper>
      </Flex>
    </TransactionModalContainer>
  );
}

export default BuySellModal;
